// src/utils/partsAnalytics.js
// Parts usage analytics and prediction system
import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../firebase";

function toPositiveNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, parsed);
}

function normalizeAction(value) {
  const upper = String(value || 'USE').toUpperCase();
  return upper === 'RESTOCK' ? 'RESTOCK' : 'USE';
}

/**
 * Log parts usage for analytics
 * @param {string} branchId - Branch ID
 * @param {string} onderdeelCode - Part code
 * @param {number} quantity - Quantity used (positive) or added (negative for restocking)
 * @param {string} action - Action type: 'USE' or 'RESTOCK'
 * @param {object} metadata - Additional metadata
 */
export async function logPartsUsage(branchId, onderdeelCode, quantity, action = 'USE', metadata = {}) {
  try {
    if (!branchId || !onderdeelCode) {
      console.warn('[PartsAnalytics] Missing required parameters');
      return;
    }

    const normalizedQuantity = toPositiveNumber(quantity);
    if (normalizedQuantity <= 0) {
      console.warn('[PartsAnalytics] Ignoring usage log with non-positive quantity');
      return;
    }

    const normalizedAction = normalizeAction(action);

    const usageEntry = {
      branchId,
      onderdeelCode,
      quantity: normalizedQuantity,
      action: normalizedAction,
      timestamp: serverTimestamp(),
      metadata: metadata || {},
      date: new Date().toISOString().split('T')[0] // YYYY-MM-DD for daily aggregation
    };

    await addDoc(collection(db, "vestigingen", branchId, "partsUsageLog"), usageEntry);
    console.log(`[PartsAnalytics] Logged ${normalizedAction} of ${normalizedQuantity}x ${onderdeelCode}`);
  } catch (error) {
    console.error('[PartsAnalytics] Failed to log usage:', error);
  }
}

/**
 * Get usage history for a specific part
 * @param {string} branchId - Branch ID
 * @param {string} onderdeelCode - Part code
 * @param {number} days - Number of days to look back
 * @returns {Promise<Array>} Usage history
 */
export async function getPartUsageHistory(branchId, onderdeelCode, days = 90) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const q = query(
      collection(db, "vestigingen", branchId, "partsUsageLog"),
      where("onderdeelCode", "==", onderdeelCode),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      orderBy("timestamp", "desc")
    );

    const snapshot = await getDocs(q);
    const docs = Array.isArray(snapshot?.docs) ? snapshot.docs : [];
    return docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[PartsAnalytics] Failed to fetch usage history:', error);
    return [];
  }
}

/**
 * Get all usage data for a branch
 * @param {string} branchId - Branch ID
 * @param {number} days - Number of days to look back
 * @returns {Promise<Array>} All usage data
 */
export async function getAllPartsUsage(branchId, days = 90) {
  try {
    const baseCollection = collection(db, "vestigingen", branchId, "partsUsageLog");
    const shouldUseAllData = days === null || days === undefined || days === 0 || days === 'all' || days === Infinity;

    const q = shouldUseAllData
      ? query(baseCollection, orderBy("timestamp", "desc"))
      : (() => {
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          return query(
            baseCollection,
            where("timestamp", ">=", Timestamp.fromDate(startDate)),
            orderBy("timestamp", "desc")
          );
        })();

    const snapshot = await getDocs(q);
    const docs = Array.isArray(snapshot?.docs) ? snapshot.docs : [];
    return docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[PartsAnalytics] Failed to fetch all usage:', error);
    return [];
  }
}

/**
 * Calculate daily usage rate for a part
 * @param {Array} usageHistory - Usage history array
 * @returns {number} Average daily usage
 */
export function calculateDailyUsageRate(usageHistory) {
  if (!usageHistory || usageHistory.length === 0) return 0;

  // Group by date and sum quantities for USE actions only
  const dailyUsage = {};
  usageHistory.forEach(entry => {
    if (entry.action === 'USE') {
      const ts = typeof entry.timestamp?.toDate === 'function' ? entry.timestamp.toDate() : (entry.timestamp ? new Date(entry.timestamp) : null);
      const date = entry.date || (ts && !Number.isNaN(ts.getTime()) ? ts.toISOString().split('T')[0] : null);
      if (!date) return;
      dailyUsage[date] = (dailyUsage[date] || 0) + entry.quantity;
    }
  });

  const days = Object.keys(dailyUsage).length;
  if (days === 0) return 0;

  const totalUsed = Object.values(dailyUsage).reduce((sum, qty) => sum + qty, 0);
  return totalUsed / days;
}

/**
 * Predict when a part will run out based on usage patterns
 * @param {number} currentStock - Current stock level
 * @param {Array} usageHistory - Usage history array
 * @param {number} minimumStock - Minimum stock threshold
 * @returns {object} Prediction result with daysUntilReorder, suggestedReorderDate, confidence
 */
export function predictReorderDate(currentStock, usageHistory, minimumStock = 5) {
  const dailyRate = calculateDailyUsageRate(usageHistory);
  const safeCurrentStock = toPositiveNumber(currentStock);
  const safeMinimumStock = toPositiveNumber(minimumStock);
  
  if (dailyRate === 0) {
    return {
      daysUntilReorder: null,
      suggestedReorderDate: null,
      confidence: 'low',
      message: 'Insufficient usage data for prediction',
      dailyUsageRate: 0
    };
  }

  // Calculate days until we hit minimum stock
  const stockAboveMinimum = safeCurrentStock - safeMinimumStock;
  const daysUntilReorder = Math.floor(stockAboveMinimum / dailyRate);

  // Calculate confidence based on data consistency
  const dataPoints = usageHistory.filter(e => e.action === 'USE').length;
  let confidence = 'low';
  if (dataPoints >= 30) confidence = 'high';
  else if (dataPoints >= 10) confidence = 'medium';

  const reorderDate = new Date();
  reorderDate.setDate(reorderDate.getDate() + daysUntilReorder);

  return {
    daysUntilReorder: Math.max(0, daysUntilReorder),
    suggestedReorderDate: reorderDate.toISOString().split('T')[0],
    confidence,
    dailyUsageRate: Number(dailyRate.toFixed(2)),
    message: daysUntilReorder <= 0 
      ? 'Stock is at or below minimum - order now!'
      : daysUntilReorder <= 7
      ? 'Low stock - consider ordering soon'
      : 'Stock levels are adequate'
  };
}

/**
 * Get usage trends for all parts in a branch
 * @param {string} branchId - Branch ID
 * @param {Array} voorraad - Current stock array
 * @param {number} days - Days to analyze
 * @returns {Promise<Array>} Array of parts with predictions
 */
export async function getPartsUsageTrends(branchId, voorraad, days = 90) {
  try {
    const allUsage = await getAllPartsUsage(branchId, days);
    
    // Group usage by part code
    const usageByPart = {};
    allUsage.forEach(entry => {
      if (!usageByPart[entry.onderdeelCode]) {
        usageByPart[entry.onderdeelCode] = [];
      }
      usageByPart[entry.onderdeelCode].push(entry);
    });

    // Calculate predictions for each part with stock data
    const trends = voorraad.map(part => {
      const usageHistory = usageByPart[part.id] || [];
      const currentStock = toPositiveNumber(part.aantal);
      const minimumStock = Math.max(1, toPositiveNumber(part.minimumStock || 5));
      
      const prediction = predictReorderDate(currentStock, usageHistory, minimumStock);
      
      return {
        code: part.id,
        naam: part.naam || part.name,
        currentStock,
        minimumStock,
        ...prediction,
        usageDataPoints: usageHistory.filter(e => e.action === 'USE').length
      };
    });

    // Sort by urgency (lowest daysUntilReorder first)
    return trends.sort((a, b) => {
      if (a.daysUntilReorder === null) return 1;
      if (b.daysUntilReorder === null) return -1;
      return a.daysUntilReorder - b.daysUntilReorder;
    });
  } catch (error) {
    console.error('[PartsAnalytics] Failed to get usage trends:', error);
    return [];
  }
}

/**
 * Get parts that need reordering soon
 * @param {Array} trends - Trends array from getPartsUsageTrends
 * @param {number} daysThreshold - Days threshold for urgent reorder
 * @returns {Array} Parts that need reordering
 */
export function getPartsNeedingReorder(trends, daysThreshold = 14) {
  return trends.filter(part => 
    part.daysUntilReorder !== null && 
    part.daysUntilReorder <= daysThreshold
  );
}
